import { FC } from "react";
import { Globe, Hand, LogOut, Mic, MicOff, User, Users } from "lucide-react";

type Rect = { top: number; left: number; width: number; height: number };

type TourDemoProps = {
    stepId: string;
    rect: Rect;
};

/**
 * Camada de DEMONSTRAÇÃO do tour: animações ilustrativas sobre o elemento real.
 * É puramente visual (pointer-events: none) e NÃO altera nenhum dado da sala.
 */
const KEYFRAMES = `
@keyframes tourSlide {
  0%   { transform: translateX(0) translateY(-50%); }
  50%  { transform: translateX(var(--tour-slide-x, 90px)) translateY(-50%); }
  100% { transform: translateX(0) translateY(-50%); }
}
@keyframes tourRipple {
  0%   { transform: scale(0.5); opacity: 0.85; }
  100% { transform: scale(1.9); opacity: 0; }
}
@keyframes tourFloat {
  0%, 100% { transform: translate(-50%, -50%); }
  50%      { transform: translate(-50%, calc(-50% - 7px)); }
}
@keyframes tourMicSwap {
  0%, 45%   { opacity: 1; }
  55%, 100% { opacity: 0; }
}
@keyframes tourMicSwapOn {
  0%, 45%   { opacity: 0; }
  55%, 100% { opacity: 1; }
}
@keyframes tourTap {
  0%, 65%, 100% { transform: scale(1); }
  80%           { transform: scale(0.8); }
}
@keyframes tourSpeak {
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 1; }
}
`;

const Caption: FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className="absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-slate-900/90 px-3 py-1 text-[11px] font-semibold text-amber-200 shadow ring-1 ring-amber-400/30">
        {children}
    </span>
);

const TourDemo: FC<TourDemoProps> = ({ stepId, rect }) => {
    const containerStyle: React.CSSProperties = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
    };
    const isTall = rect.height >= 110;

    const renderInner = () => {
        switch (stepId) {
            case "scene":
                return (
                    <>
                        <div
                            className="absolute top-1/2"
                            style={{
                                left: "16%",
                                ["--tour-slide-x" as any]: `${Math.min(rect.width * 0.4, 170)}px`,
                                animation: "tourSlide 2.6s ease-in-out infinite",
                            }}
                        >
                            <div className="relative">
                                <img
                                    src="/assets/avatars/avatar_1/front_standing.png"
                                    alt=""
                                    className="h-24 w-auto select-none drop-shadow-[0_6px_10px_rgba(0,0,0,0.5)]"
                                    draggable={false}
                                    onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.display = "none";
                                    }}
                                />
                                <span className="absolute -bottom-2 left-1/2 h-8 w-8 -translate-x-1/2">
                                    <span
                                        className="absolute inset-0 rounded-full bg-amber-400/60"
                                        style={{ animation: "tourRipple 1.3s ease-out infinite" }}
                                    />
                                    <Hand className="absolute inset-0 m-auto h-5 w-5 text-white drop-shadow" />
                                </span>
                            </div>
                        </div>
                        <Caption>Arraste para posicionar o avatar</Caption>
                    </>
                );

            case "video":
                return (
                    <>
                        <div
                            className="absolute left-1/2 top-1/2"
                            style={{ animation: "tourFloat 2.6s ease-in-out infinite" }}
                        >
                            <div className="flex items-end gap-2">
                                {/* Tile principal: você falando */}
                                <div className="relative h-[78px] w-[104px] overflow-hidden rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 shadow-xl ring-1 ring-white/20">
                                    <span
                                        className="absolute inset-0 rounded-xl ring-2 ring-emerald-400"
                                        style={{ animation: "tourSpeak 1.1s ease-in-out infinite" }}
                                    />
                                    <div className="flex h-full items-center justify-center">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-400/80">
                                            <User className="h-6 w-6 text-slate-800" />
                                        </div>
                                    </div>
                                    <div className="absolute inset-x-1 bottom-1 flex items-center justify-between">
                                        <span className="rounded bg-black/40 px-1 text-[9px] font-medium text-white">
                                            Você
                                        </span>
                                        <Mic className="h-3.5 w-3.5 text-emerald-300" />
                                    </div>
                                </div>
                                {/* Tile secundário: outro participante */}
                                <div className="relative h-[58px] w-[78px] overflow-hidden rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 shadow-lg ring-1 ring-white/10">
                                    <div className="flex h-full items-center justify-center">
                                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-500/80">
                                            <User className="h-4 w-4 text-slate-900" />
                                        </div>
                                    </div>
                                    <div className="absolute inset-x-1 bottom-1 flex items-center justify-between">
                                        <span className="rounded bg-black/40 px-1 text-[8px] font-medium text-white">
                                            Grupo
                                        </span>
                                        <Mic className="h-3 w-3 text-emerald-300" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {isTall && <Caption>Você se vê e ouve o grupo aqui</Caption>}
                    </>
                );

            case "mic":
                // A faixa é fina: apontamos a mão para o botão real "Ligar microfone" (à direita).
                return (
                    <div
                        className="absolute top-1/2 -translate-y-1/2"
                        style={{ left: "min(82%, calc(100% - 70px))" }}
                    >
                        <div className="relative" style={{ animation: "tourTap 1.2s ease-in-out infinite" }}>
                            <span
                                className="absolute -inset-2 rounded-full bg-amber-300/60"
                                style={{ animation: "tourRipple 1.2s ease-out infinite" }}
                            />
                            <Hand className="h-6 w-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]" />
                        </div>
                    </div>
                );

            case "actions":
            case "moderator": {
                const chip =
                    "relative flex h-9 w-9 items-center justify-center rounded-full shadow-lg ring-1 ring-white/20";
                return (
                    <>
                        <div
                            className="absolute left-1/2 top-1/2"
                            style={{ animation: "tourFloat 2.6s ease-in-out infinite" }}
                        >
                            <div className="flex items-center gap-2.5 rounded-2xl bg-slate-900/85 px-3 py-2 ring-1 ring-white/15">
                                <span className={`${chip} bg-green-600`}>
                                    <Globe className="h-4 w-4 text-white" />
                                </span>
                                <span className={`${chip} bg-blue-700`}>
                                    <Users className="h-4 w-4 text-white" />
                                    <span
                                        className="absolute inset-0 rounded-full bg-amber-300/50"
                                        style={{ animation: "tourRipple 1.2s ease-out infinite" }}
                                    />
                                    {/* mão tocando o botão central */}
                                    <span
                                        className="absolute -right-1.5 top-1/2 -translate-y-1/2"
                                        style={{ animation: "tourTap 1.2s ease-in-out infinite" }}
                                    >
                                        <Hand className="h-6 w-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]" />
                                    </span>
                                </span>
                                <span className={`${chip} bg-red-600`}>
                                    <LogOut className="h-4 w-4 text-white" />
                                </span>
                            </div>
                        </div>
                        {isTall && <Caption>Toque para abrir os controles da sala</Caption>}
                    </>
                );
            }

            default:
                return null;
        }
    };

    const inner = renderInner();
    if (!inner) {
        return null;
    }

    return (
        <>
            <style>{KEYFRAMES}</style>
            <div
                className="pointer-events-none fixed z-[81] overflow-hidden rounded-xl"
                style={containerStyle}
                aria-hidden
            >
                {inner}
            </div>
        </>
    );
};

export default TourDemo;
